import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Inline SVG flag — Windows doesn't render regional indicator emojis (🇫🇷)
const FrFlag = ({ className = 'w-4 h-4' }: { className?: string }) => (
    <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg" aria-label="France">
        <rect width="1" height="2" fill="#002395" />
        <rect x="1" width="1" height="2" fill="#fff" />
        <rect x="2" width="1" height="2" fill="#ED2939" />
    </svg>
);

/**
 * WaitlistBottomSections
 * 
 * Extracted from WaitlistPage.tsx — contains:
 *   - Mission statement
 *   - FAQ accordion
 *   - Final CTA
 *   - Manifesto link
 *   - "Built in France" compliance banner
 * 
 * Self-contained with its own FAQ accordion state.
 */
const WaitlistBottomSections: React.FC = () => {
    const { t } = useTranslation('marketing');
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    return (
        <>
            {/* ═══════════ MISSION ═══════════ */}
            <section className="relative py-14 md:py-20" id="mission">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                <div className="container-hig">
                    <div className="max-w-2xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-8 tracking-tight"
                        >
                            {t('waitlist.mission_title')}
                        </motion.h2>
                        <div className="space-y-5">
                            <p className="text-sm md:text-base text-hig-text-tertiary leading-relaxed">
                                {t('waitlist.mission_p1')}
                            </p>
                            <p className="text-lg text-hig-text-primary font-semibold">
                                {t('waitlist.mission_p2')}
                            </p>
                            <p className="text-sm text-hig-text-tertiary leading-relaxed">
                                {t('waitlist.mission_p3')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════ FAQ ═══════════ */}
            <section className="relative py-14 md:py-20" id="faq">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                <div className="container-hig">
                    <div className="max-w-2xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-10 tracking-tight"
                        >
                            {t('waitlist.faq_title')}
                        </motion.h2>

                        <div className="space-y-2">
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                                <motion.div
                                    key={n}
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                    transition={{ duration: 0.4, delay: n * 0.04 }}
                                    className="rounded-xl border border-hig-border bg-hig-bg-tertiary/20 overflow-hidden"
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === n ? null : n)}
                                        className="w-full flex items-center justify-between p-4 text-left group hover:bg-hig-bg-tertiary/20 transition-colors"
                                    >
                                        <span className="text-sm font-medium text-hig-text-secondary group-hover:text-hig-text-primary transition-colors">
                                            {t(`waitlist.faq_q${n}`)}
                                        </span>
                                        <ChevronDown
                                            className={`w-4 h-4 text-hig-text-tertiary transition-transform duration-300 flex-shrink-0 ml-4 ${openFaq === n ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    <AnimatePresence>
                                        {openFaq === n && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                className="overflow-hidden"
                                            >
                                                <p className="px-4 pb-4 text-[12px] text-hig-text-tertiary leading-relaxed">
                                                    {t(`waitlist.faq_a${n}`)}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════ FINAL CTA ═══════════ */}
            <section className="relative py-14 md:py-20" id="final-cta">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                <div className="container-hig">
                    <div className="max-w-xl mx-auto text-center">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-6 tracking-tight"
                        >
                            {t('waitlist.final_cta_title')}
                        </motion.h2>
                        <p className="text-sm text-hig-text-tertiary leading-relaxed mb-8">
                            {t('waitlist.final_cta_body')}
                        </p>
                        <a
                            href="#hero"
                            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-medium text-hig-text-primary bg-hig-accent hover:bg-[rgba(0,229,255,0.88)] transition-colors"
                        >
                            {t('waitlist.cta')}
                            <ArrowRight className="w-3.5 h-3.5" />
                        </a>
                        <p className="text-[11px] text-hig-text-tertiary mt-4 tracking-wide">
                            {t('waitlist.final_cta_microcopy')}
                        </p>
                    </div>
                </div>
            </section>

            {/* ═══════════ MANIFESTO ═══════════ */}
            <section className="relative py-6">
                <div className="container-hig text-center">
                    <p className="text-sm text-hig-text-tertiary font-medium tracking-wide mb-2">
                        {t('waitlist.manifesto')}
                    </p>
                    <a
                        href="/manifesto"
                        className="inline-flex items-center gap-1.5 text-xs text-hig-accent/70 hover:text-hig-accent transition-colors mt-1"
                        style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 }}
                    >
                        {t('manifesto.hero_title', 'The End of Generic AI')} →
                    </a>
                </div>
            </section>

            {/* ═══════════ BUILT IN FRANCE. COMPLIANCE BANNER ═══════════ */}
            <section className="relative py-8">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent" />
                <div className="container-hig">
                    <div className="max-w-3xl mx-auto">
                        {/* Main line */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
                            <FrFlag className="w-8 h-8" />
                            <p className="text-sm font-semibold text-center sm:text-left tracking-wide" style={{ color: 'var(--text-primary)' }}>
                                {t('waitlist.compliance_section_title', 'Built in France. Governed by Law.')}
                            </p>
                        </div>
                        <p className="text-xs text-center max-w-xl mx-auto mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                            {t('waitlist.compliance_section_sub')}
                        </p>
                        {/* Badge row */}
                        <div className="flex flex-wrap items-center justify-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wider uppercase" style={{ border: '1px solid rgba(96,165,250,0.4)', background: 'rgba(96,165,250,0.10)', color: '#93c5fd' }}>
                                <FrFlag className="w-3 h-3 mr-0.5 inline-block" /> {t('waitlist.compliance_badge_france_label', 'Made in France')}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wider uppercase" style={{ border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.10)', color: '#fbbf24' }}>
                                ⚖️ {t('waitlist.compliance_badge_aiact_label', 'EU AI Act Ready')}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wider uppercase" style={{ border: '1px solid rgba(52,211,153,0.4)', background: 'rgba(52,211,153,0.10)', color: '#6ee7b7' }}>
                                🔐 {t('waitlist.compliance_badge_gdpr_label', 'GDPR by Design')}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wider uppercase" style={{ border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.10)', color: '#c4b5fd' }}>
                                📋 {t('waitlist.compliance_badge_inpi_label', '175+ INPI Claims')}
                            </span>
                        </div>
                        <p className="text-[10px] text-center mt-5 tracking-wide" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
                            {t('waitlist.compliance_footer_note')}
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
};

export default WaitlistBottomSections;
